<div class="terminal-payment-modal" [ngStyle]="themeStyle">
  <div class="modal-header">
    <div class="header-content">
      <div class="header-icon-wrap">
        <mat-icon class="header-icon">credit_card</mat-icon>
      </div>
      <div class="header-text">
        <h2 class="modal-title">Card Terminal Payment</h2>
        <p class="terminal-name" *ngIf="terminal?.terminal_name">{{ terminal?.terminal_name }}</p>
      </div>
    </div>
    <button
      type="button"
      class="close-button"
      (click)="cancelTransaction()"
      [disabled]="status?.status === 'approved' || status?.status === 'partial_approved'"
      matTooltip="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <div class="modal-body">
    <div class="payment-amount-card">
      <div class="amount-label">Amount to Charge</div>
      <div class="amount-value">{{ amount | currency }}</div>
    </div>

    <!-- Loading State -->
    <div class="status-container" *ngIf="!status && !errorMessage">
      <mat-spinner diameter="60"></mat-spinner>
      <p class="status-text mt-3">Initializing transaction...</p>
    </div>

    <!-- Error State (No Status) -->
    <div class="status-container error-state" *ngIf="errorMessage && !status">
      <mat-icon class="status-icon error">error</mat-icon>
      <h3 class="status-title">Connection Error</h3>
      <p class="status-message">{{ errorMessage }}</p>
      <div class="manual-accept-note" *ngIf="manualAcceptAvailable">
        <mat-icon>warning</mat-icon>
        <span>If the terminal shows the payment was approved, add it here instead of retrying.</span>
      </div>
    </div>

    <!-- Transaction Status -->
    <div class="status-container" *ngIf="status">
      <!-- Pending/Waiting/Processing States -->
      <ng-container *ngIf="status.status === 'pending' || status.status === 'waiting_for_card' || status.status === 'processing'">
        <mat-spinner diameter="56" class="state-spinner"></mat-spinner>
        <h3 class="status-title">{{ getStatusMessage() }}</h3>
        <p class="status-message">Follow the prompts on the terminal to complete the payment.</p>
      </ng-container>

      <!-- Approved State -->
      <ng-container *ngIf="status.status === 'approved'">
        <div class="status-icon-wrapper success">
          <mat-icon class="status-icon success">check_circle</mat-icon>
        </div>
        <h3 class="status-title success">Payment Approved!</h3>
        
        <div class="transaction-details">
          <div class="detail-row" *ngIf="status.entry_method">
            <mat-icon class="detail-icon">{{ getEntryMethodIcon(status.entry_method) }}</mat-icon>
            <span class="detail-label text-capitalize">{{ status.entry_method?.replace('_', ' ') }}</span>
          </div>
          
          <div class="detail-row">
            <span class="detail-label">Authorization Code</span>
            <span class="detail-value">{{ status.auth_code }}</span>
          </div>
          
          <div class="detail-row" *ngIf="status.card_type">
            <span class="detail-label">Card Type</span>
            <span class="detail-value">{{ status.card_type }}</span>
          </div>
          
          <div class="detail-row" *ngIf="status.card_last_four">
            <span class="detail-label">Card Number</span>
            <span class="detail-value">****{{ status.card_last_four }}</span>
          </div>
          
          <div class="detail-row" *ngIf="status.total_amount">
            <span class="detail-label">Total Amount</span>
            <span class="detail-value">{{ status.total_amount | currency }}</span>
          </div>
          
          <div class="detail-row" *ngIf="status.tip_amount">
            <span class="detail-label">Tip</span>
            <span class="detail-value">{{ status.tip_amount | currency }}</span>
          </div>
          
          <div class="detail-row" *ngIf="status.reference">
            <span class="detail-label">Reference</span>
            <span class="detail-value">{{ status.reference }}</span>
          </div>
          
          <div class="detail-footer" *ngIf="status.emv_app_label">
            <small class="text-muted">{{ status.emv_app_label }}</small>
          </div>
          
          <div class="demo-badge" *ngIf="status.demo_mode">
            <mat-icon>info</mat-icon>
            <span>DEMO MODE</span>
          </div>
        </div>
      </ng-container>

      <!-- Partial Approved State -->
      <ng-container *ngIf="status.status === 'partial_approved'">
        <div class="status-icon-wrapper warning">
          <mat-icon class="status-icon warning">check_circle</mat-icon>
        </div>
        <h3 class="status-title warning">Partially Approved</h3>
        
        <div class="transaction-details">
          <div class="detail-row highlight">
            <span class="detail-label">Amount Due</span>
            <span class="detail-value">{{ status.amount_due | currency }}</span>
          </div>
          
          <div class="detail-row" *ngIf="status.auth_code">
            <span class="detail-label">Authorization Code</span>
            <span class="detail-value">{{ status.auth_code }}</span>
          </div>
          
          <div class="detail-row" *ngIf="status.card_last_four">
            <span class="detail-label">Card Number</span>
            <span class="detail-value">****{{ status.card_last_four }}</span>
          </div>
        </div>
      </ng-container>

      <!-- Declined State -->
      <ng-container *ngIf="status.status === 'declined'">
        <div class="status-icon-wrapper error">
          <mat-icon class="status-icon error">cancel</mat-icon>
        </div>
        <h3 class="status-title error">Transaction Declined</h3>
        <p class="status-message">{{ status.message || status.response_text || 'The card was declined. Please try another payment method.' }}</p>
        <p class="status-code" *ngIf="status.response_code">Response Code: {{ status.response_code }}</p>
      </ng-container>

      <!-- Error State -->
      <ng-container *ngIf="status.status === 'error'">
        <div class="status-icon-wrapper error">
          <mat-icon class="status-icon error">error</mat-icon>
        </div>
        <h3 class="status-title error">Transaction Error</h3>
        <p class="status-message">{{ status.message || status.response_text || 'An error occurred while processing the payment.' }}</p>
        <p class="status-code" *ngIf="status.response_code">Response Code: {{ status.response_code }}</p>
        <div class="manual-accept-note" *ngIf="manualAcceptAvailable">
          <mat-icon>warning</mat-icon>
          <span>If the terminal shows the payment was approved, add it here instead of retrying.</span>
        </div>
      </ng-container>

      <!-- Cancelled State -->
      <ng-container *ngIf="status.status === 'cancelled'">
        <div class="status-icon-wrapper cancelled">
          <mat-icon class="status-icon cancelled">block</mat-icon>
        </div>
        <h3 class="status-title cancelled">Transaction Cancelled</h3>
        <p class="status-message">The payment was cancelled.</p>
      </ng-container>
    </div>
  </div>

  <div class="modal-footer">
    <button
      mat-stroked-button
      color="warn"
      (click)="cancelTransaction()"
      *ngIf="status?.status !== 'approved' && status?.status !== 'partial_approved' && status?.status !== 'processing'">
      <mat-icon>close</mat-icon>
      Cancel
    </button>
    <button
      mat-raised-button
      color="primary"
      (click)="retryTransaction()"
      *ngIf="status?.status === 'declined' || status?.status === 'error' || errorMessage">
      <mat-icon>refresh</mat-icon>
      Retry Payment
    </button>
    <button
      mat-raised-button
      color="accent"
      (click)="acceptTerminalPayment()"
      *ngIf="manualAcceptAvailable">
      <mat-icon>playlist_add_check</mat-icon>
      Add Payment
    </button>
    <button
      mat-raised-button
      color="primary"
      (click)="activeModal.close({ success: true })"
      *ngIf="status?.status === 'approved' || status?.status === 'partial_approved'">
      <mat-icon>check</mat-icon>
      Continue
    </button>
  </div>
</div>

